import get from "lodash/get"
import camelCase from "lodash/camelCase"
import { createRef, Component } from "react"
import PropTypes from "prop-types"
import MaskedInput from "react-text-mask"
import { ReactSVG } from "react-svg"
import getContentUrl from "utils-lib/getContentUrl"
import joinClassNames from "utils-lib/joinClassNames"
import "./TextInput.scss"

export default class TextInput extends Component {
  constructor(props) {
    super(props)
    this.state = {
      visited: false,
      errorMessage: ``,
    }

    this.inputRef = createRef()
  }

  componentDidMount() {
    // this.inputRef.current.addEventListener(`change`, this.onChange)

    this.inputInterval = setInterval(this.pollInput, 1000)
  }

  componentWillUnmount() {
    // this.inputRef.current.removeEventListener(`change`, this.onChange)

    clearInterval(this.inputInterval)
  }

  // eslint-disable-next-line no-unused-vars
  componentDidUpdate(prevProps, prevState) {
    if (this.state.visited && prevProps.language !== this.props.language) {
      this.checkError(this.props.value)
    }
  }

  getInput() {
    return this.inputRef.current
  }

  pollInput = () => {
    if (get(this.inputRef, `current.value`, ``)) {
      if (this.inputRef.current.value !== this.props.value) {
        this.onChange({ target: this.inputRef.current })
      }
    }
  }

  checkError = (value) => {
    const errorMessage = this.props.getErrorMessage(value)

    this.setState({
      errorMessage: this.props.getErrorMessage(value),
    })
    this.props.onValidate(!errorMessage)
  }

  onClick() {
    this.props.onButtonClick()
  }

  onChange = (e) => {
    if (this.props.passEvent) {
      this.props.onChange(e)
      if (this.state.visited) {
        this.checkError(e)
      }
    } else {
      this.props.onChange(e.target.value)
      if (this.state.visited) {
        this.checkError(e.target.value?.trim())
      }
    }
  }

  renderFieldButton() {
    const {
      buttonChildren,
      buttonTabIndex,
      buttonStyle,
      customErrorMessage,
      disableValidation,
    } = this.props

    return (
      <button
        data-testid="TextInput - button"
        className="btn-right btn-right-ie maax-font-weight-regular"
        style={{
          ...buttonStyle,
          position: `relative`,
          left:
            !disableValidation &&
            (this.state.errorMessage || customErrorMessage)
              ? `-1.5rem`
              : 0,
          top: `-.4rem`,
          transition: `.3s left`,
        }}
        tabIndex={buttonTabIndex}
        onClick={() => {
          this.onClick()
        }}
        type="button"
      >
        {buttonChildren}
      </button>
    )
  }

  render() {
    const {
      buttonChildren,
      className,
      customErrorMessage,
      inputClassName,
      inputStyle,
      inputType,
      isControlled,
      isEmailField,
      isPasswordField,
      label,
      labelStyle,
      readOnly,
      showPassword,
      style,
      value,
      inputMaxLength,
      disableValidation,
      enableFloatLabel,
      isMaskedInput,
      id,
      mask,
      handleKeyDown,
      onFocus,
      onBlur,
      guide,
      placeholderChar,
      disabled,
      // eslint-disable-next-line no-unused-vars
      inputID,
      hideNoError,
    } = this.props

    const { errorMessage } = this.state

    const finalErrorMessage = customErrorMessage || errorMessage

    let inputClassNames = joinClassNames([
      `inputMaterial`,
      finalErrorMessage !== `` &&
      finalErrorMessage !== undefined &&
      !disableValidation
        ? `error`
        : null,
      isPasswordField ? `password` : null,
    ])

    if (inputClassName !== ``) {
      inputClassNames += ` ${inputClassName}`
    }

    const classNames = `TextInput${className !== `` ? ` ${className}` : ``}`

    let finalInputType = inputType
    if (isPasswordField && !showPassword) {
      finalInputType = `password`
    } else if (isEmailField) {
      finalInputType = `email`
    }

    const maxLength = inputMaxLength !== `` ? inputMaxLength : ``

    const handlerProps = readOnly
      ? {}
      : {
          onChange: this.onChange,
          onFocus: onFocus || this.onFocus,
          onBlur: (e) => {
            /* Mark field as visited. */
            this.setState({
              visited: true,
            })
            onBlur(e)
            this.checkError(e.target.value)
          },
          onKeyDown:
            handleKeyDown ||
            ((e) => {
              if (e.charCode === 13 || e.keyCode === 13) {
                // this.props.handleSubmit()
              }
            }),
          onInput: (e) => {
            if (errorMessage !== ``) {
              this.checkError(e.target.value)
            }
          },
        }

    const inputProps = isMaskedInput
      ? {
          id,
          mask,
          guide,
          placeholderChar,
          maxLength,
          disabled,
          handleKeyDown,
          className: inputClassNames,
          type: finalInputType,
          readOnly,
          ...handlerProps,
        }
      : {
          className: inputClassNames,
          type: finalInputType,
          readOnly,
          maxLength,
          disabled,
          ...handlerProps,
        }

    /* Don't set the value for password fields to prevent the password displaying in the browser inspect tool. */
    /* Makes email input uncontrolled when not remembered to avoid overriding iOS autofill */
    if (isControlled) {
      inputProps.value = value
    }

    return (
      <div className={classNames} style={style} data-testid="TextInput">
        {isMaskedInput ? (
          <MaskedInput
            data-testid="TextInput - MaskedInput"
            ref={this.inputRef}
            {...inputProps}
            placeholder={enableFloatLabel ? `` : label}
            style={inputStyle}
            id={camelCase(label)}
            aria-describedby={`error_${camelCase(label)}`}
          />
        ) : (
          <input
            ref={this.inputRef}
            {...inputProps}
            placeholder={enableFloatLabel ? `` : label}
            style={inputStyle}
            id={camelCase(label)}
            aria-describedby={`error_${camelCase(label)}`}
          />
        )}
        {enableFloatLabel ? (
          <label
            data-testid="TextInput - label"
            className={get(value, `length`, 0) > 0 ? `focused-label` : ``}
            style={labelStyle}
            htmlFor={camelCase(label)}
          >
            {label}
          </label>
        ) : null}
        {buttonChildren !== null ? this.renderFieldButton() : null}
        {disableValidation ? null : (
          <div
            data-testid="TextInput - validation"
            className="TextInput__error-message pt-2"
            style={{
              minHeight:
                hideNoError && !finalErrorMessage ? `.25rem` : `2.5rem`,
            }}
          >
            {finalErrorMessage ? (
              <ReactSVG
                wrapper="span"
                src={getContentUrl(
                  "/content/dam/mywm/icons/red-alert-icon.svg",
                )}
                style={{
                  position: `absolute`,
                  right: `.625rem`,
                  top: `.8rem`,
                }}
              />
            ) : null}
            <div className="validation-error" id={`error_${camelCase(label)}`}>
              {finalErrorMessage}
            </div>
          </div>
        )}
      </div>
    )
  }
}

TextInput.propTypes = {
  /* Required props. */
  value: PropTypes.string.isRequired,

  /* Optional props. */
  disableValidation: PropTypes.bool,
  label: PropTypes.string,
  buttonChildren: PropTypes.node,
  buttonStyle: PropTypes.object,
  buttonTabIndex: PropTypes.number,
  className: PropTypes.string,
  customErrorMessage: PropTypes.string,
  getErrorMessage: PropTypes.func,
  inputClassName: PropTypes.string,
  inputStyle: PropTypes.object,
  inputType: PropTypes.string,
  isControlled: PropTypes.bool,
  isEmailField: PropTypes.bool,
  isPasswordField: PropTypes.bool,
  labelClassName: PropTypes.string,
  labelStyle: PropTypes.object,
  language: PropTypes.string,
  onButtonClick: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  readOnly: PropTypes.bool,
  showPassword: PropTypes.bool,
  style: PropTypes.object,
  inputMaxLength: PropTypes.string,
  enableFloatLabel: PropTypes.bool,
  /* maskedInput */
  isMaskedInput: PropTypes.bool,
  id: PropTypes.string,
  mask: PropTypes.string,
  handleKeyDown: PropTypes.func,
  guide: PropTypes.bool,
  placeholderChar: PropTypes.string,
  disabled: PropTypes.bool,
  passEvent: PropTypes.bool,
  inputID: PropTypes.string,
  hideNoError: PropTypes.bool,
}

TextInput.defaultProps = {
  buttonChildren: null,
  buttonTabIndex: 0,
  buttonStyle: {},
  className: ``,
  customErrorMessage: ``,
  disableValidation: false,
  getErrorMessage: () => {},
  inputClassName: ``,
  inputStyle: {},
  inputType: `text`,
  isControlled: true,
  isEmailField: false,
  isPasswordField: false,
  labelClassName: ``,
  language: ``,
  onButtonClick: () => {},
  onBlur: () => {},
  onChange: () => {},
  onValidate: () => {},
  readOnly: false,
  showPassword: false,
  style: {},
  inputMaxLength: ``,
  enableFloatLabel: true,
  isMaskedInput: false,
  id: ``,
  mask: ``,
  onFocus: () => {},
  handleKeyDown: () => {},
  guide: false,
  placeholderChar: ` `,
  disabled: false,
  passEvent: false,
  labelStyle: {},
  label: ``,
  inputID: ``,
  hideNoError: false,
}
